import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Badge,
    Button,
    Card,
    Col,
    Container,
    Form,
    ListGroup,
    Modal,
    OverlayTrigger,
    Row,
    Table,
    Tab,
    Tabs,
    Tooltip,
} from "react-bootstrap";
import { parseISO, format, formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import SidebarPanelNavigation from "../../../components/SidebarPanelNavigation";
import LoadingIndicator from "../../../components/LoadingIndicator";
import useNotification from "../../../contexts/notification";
import {
    enviarMensagemPersonalizada,
    getHistoricoMensagens,
} from "../../../services/api";

function formatDateTime(value) {
    if (!value) {
        return "-";
    }

    try {
        return format(parseISO(value), "dd/MM/yyyy HH:mm");
    } catch (error) {
        return "-";
    }
}

function MessagePreviewModal({ show, onHide, historico }) {
    if (!historico) {
        return null;
    }

    const leadName = historico.lead?.name || "Lead removido";

    return (
        <Modal show={show} onHide={onHide} centered size="lg">
            <Modal.Header closeButton>
                <Modal.Title>Mensagem enviada</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <h5 className="mb-1">{leadName}</h5>
                <p className="text-muted mb-3">{historico.lead?.phone}</p>
                <div className="p-3 bg-light rounded" style={{ whiteSpace: "pre-wrap" }}>
                    {historico.mensagem}
                </div>
                <p className="text-muted mb-0 mt-3">
                    Enviada em {formatDateTime(historico.enviada_em)} —
                    {" "}
                    {historico.foi_automatica ? "Automática" : "Manual"}
                </p>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="outline-secondary" onClick={onHide}>
                    Fechar
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

export default function MessageHistoryAdmin() {
    const [loading, setLoading] = useState(true);
    const [history, setHistory] = useState([]);
    const [search, setSearch] = useState("");
    const [typeFilter, setTypeFilter] = useState("all");
    const [dateFilter, setDateFilter] = useState("");
    const [selectedMessage, setSelectedMessage] = useState(null);
    const [autoRefresh, setAutoRefresh] = useState(true);
    const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
    const [conversationLeadId, setConversationLeadId] = useState(null);
    const [conversationSearch, setConversationSearch] = useState("");
    const [activeTab, setActiveTab] = useState("table");
    const [manualMessage, setManualMessage] = useState("");
    const [sendingManual, setSendingManual] = useState(false);
    const { setMessage } = useNotification();
    const requestInFlightRef = useRef(false);

    const loadHistory = useCallback(
        ({ silent = false } = {}) => {
            if (requestInFlightRef.current) {
                return;
            }

            requestInFlightRef.current = true;

            if (!silent) {
                setLoading(true);
            }

            getHistoricoMensagens()
                .then((response) => {
                    const sortedHistory = [...response].sort((a, b) => {
                        const dateA = a?.enviada_em ? Date.parse(a.enviada_em) : 0;
                        const dateB = b?.enviada_em ? Date.parse(b.enviada_em) : 0;
                        return dateB - dateA;
                    });

                    setHistory(sortedHistory);
                    setLastUpdatedAt(new Date().toISOString());
                })
                .catch(() => {
                    if (!silent) {
                        setMessage({
                            error: true,
                            content: "Não foi possível carregar o histórico de mensagens.",
                        });
                    }
                })
                .finally(() => {
                    requestInFlightRef.current = false;
                    if (!silent) {
                        setLoading(false);
                    }
                });
        },
        [setMessage]
    );

    useEffect(() => {
        loadHistory();
    }, [loadHistory]);

    useEffect(() => {
        if (!autoRefresh) {
            return undefined;
        }

        const intervalId = setInterval(() => {
            loadHistory({ silent: true });
        }, 15000);

        return () => clearInterval(intervalId);
    }, [autoRefresh, loadHistory]);

    const filteredHistory = useMemo(() => {
        const normalizedSearch = search.trim().toLowerCase();

        return history.filter((item) => {
            const matchesSearch = normalizedSearch
                ? [item.lead?.name, item.lead?.phone, item.mensagem]
                    .filter(Boolean)
                    .some((value) => value.toLowerCase().includes(normalizedSearch))
                : true;

            const matchesType =
                typeFilter === "all"
                    ? true
                    : item.foi_automatica === (typeFilter === "automatic");

            const matchesDate = dateFilter
                ? (() => {
                    try {
                        return (
                            format(parseISO(item.enviada_em), "yyyy-MM-dd") === dateFilter
                        );
                    } catch (error) {
                        return false;
                    }
                })()
                : true;

            return matchesSearch && matchesType && matchesDate;
        });
    }, [history, search, typeFilter, dateFilter]);

    const conversationGroups = useMemo(() => {
        const map = new Map();

        history.forEach((item) => {
            const leadId = item.lead?.id ?? `removed-${item.lead?.phone ?? "unknown"}`;
            const existing = map.get(leadId);

            const sentTimestamp = item.enviada_em ? Date.parse(item.enviada_em) : 0;

            if (!existing) {
                map.set(leadId, {
                    leadId,
                    leadName: item.lead?.name || "Lead removido",
                    leadPhone: item.lead?.phone || "-",
                    messages: [item],
                    automaticCount: item.foi_automatica ? 1 : 0,
                    manualCount: item.foi_automatica ? 0 : 1,
                    lastMessageDate: sentTimestamp,
                    lastMessageText: item.mensagem || "",
                });
                return;
            }

            map.set(leadId, {
                ...existing,
                messages: [...existing.messages, item],
                automaticCount: existing.automaticCount + (item.foi_automatica ? 1 : 0),
                manualCount: existing.manualCount + (item.foi_automatica ? 0 : 1),
                lastMessageDate:
                    existing.lastMessageDate && existing.lastMessageDate > sentTimestamp
                        ? existing.lastMessageDate
                        : sentTimestamp,
                lastMessageText:
                    existing.lastMessageDate && existing.lastMessageDate > sentTimestamp
                        ? existing.lastMessageText
                        : item.mensagem || existing.lastMessageText,
            });
        });

        return Array.from(map.values())
            .map((conversation) => ({
                ...conversation,
                messages: [...conversation.messages].sort((a, b) => {
                    const dateA = a?.enviada_em ? Date.parse(a.enviada_em) : 0;
                    const dateB = b?.enviada_em ? Date.parse(b.enviada_em) : 0;
                    return dateA - dateB;
                }),
            }))
            .sort((a, b) => (b.lastMessageDate || 0) - (a.lastMessageDate || 0));
    }, [history]);

    const filteredConversations = useMemo(() => {
        const normalized = conversationSearch.trim().toLowerCase();

        return conversationGroups.filter((conversation) => {
            if (!normalized) {
                return true;
            }

            return [conversation.leadName, conversation.leadPhone]
                .filter(Boolean)
                .some((value) => value.toLowerCase().includes(normalized));
        });
    }, [conversationGroups, conversationSearch]);

    useEffect(() => {
        if (conversationGroups.length === 0) {
            setConversationLeadId(null);
            return;
        }

        const exists = conversationGroups.some(
            (conversation) => conversation.leadId === conversationLeadId
        );

        if (!exists) {
            setConversationLeadId(conversationGroups[0].leadId);
        }
    }, [conversationGroups, conversationLeadId]);

    useEffect(() => {
        if (filteredConversations.length === 0) {
            return;
        }

        const visible = filteredConversations.some(
            (conversation) => conversation.leadId === conversationLeadId
        );

        if (!visible) {
            setConversationLeadId(filteredConversations[0].leadId);
        }
    }, [filteredConversations, conversationLeadId]);

    const selectedConversation = useMemo(
        () =>
            conversationGroups.find(
                (conversation) => conversation.leadId === conversationLeadId
            ) || null,
        [conversationGroups, conversationLeadId]
    );

    const selectedLeadId = useMemo(() => {
        if (!selectedConversation) {
            return null;
        }

        return Number.isInteger(selectedConversation.leadId)
            ? selectedConversation.leadId
            : null;
    }, [selectedConversation]);

    useEffect(() => {
        setManualMessage("");
    }, [conversationLeadId]);

    const totalMessages = history.length;
    const autoMessages = history.filter((item) => item.foi_automatica).length;
    const manualMessages = totalMessages - autoMessages;

    const lastUpdatedDescription = lastUpdatedAt
        ? formatDistanceToNow(parseISO(lastUpdatedAt), {
            addSuffix: true,
            locale: ptBR,
        })
        : "-";

    const handleSendManualMessage = async (event) => {
        event.preventDefault();

        const trimmedMessage = manualMessage.trim();

        if (!selectedLeadId) {
            setMessage({
                error: true,
                content: "Não é possível enviar mensagem para este contato.",
            });
            return;
        }

        if (!trimmedMessage) {
            setMessage({
                error: true,
                content: "Digite uma mensagem para enviar.",
            });
            return;
        }

        setSendingManual(true);

        try {
            await enviarMensagemPersonalizada({
                leadIds: [selectedLeadId],
                mensagem: trimmedMessage,
            });

            setManualMessage("");
            setMessage({
                error: false,
                content: "Mensagem enviada com sucesso.",
            });

            loadHistory({ silent: true });
        } catch (error) {
            setMessage({
                error: true,
                content: "Não foi possível enviar a mensagem.",
            });
        } finally {
            setSendingManual(false);
        }
    };

    return (

        <Container fluid>
            <div>
                <SidebarPanelNavigation />
                <div className="content-panel">
                    {loading ? (
                        <LoadingIndicator />
                    ) : (
                        <>
                            <Row className="align-items-center">
                                <Col md={8} className="mb-3">
                                    <h2 className="mb-1">Histórico de mensagens</h2>
                                    <p className="text-muted mb-0">
                                        Consulte todas as mensagens enviadas pelo funil de automação e acompanhe o engajamento dos leads.
                                    </p>
                                </Col>
                                <Col
                                    md={4}
                                    className="d-flex flex-column align-items-md-end align-items-start mb-3"
                                >
                                    <OverlayTrigger
                                        placement="bottom"
                                        overlay={
                                            <Tooltip id="auto-refresh-tooltip">
                                                Atualiza a lista automaticamente a cada 15 segundos.
                                            </Tooltip>
                                        }
                                    >
                                        <Form.Check
                                            type="switch"
                                            id="auto-refresh-toggle"
                                            label="Atualização automática"
                                            checked={autoRefresh}
                                            onChange={(event) => setAutoRefresh(event.target.checked)}
                                            className="mb-2"
                                        />
                                    </OverlayTrigger>
                                    <div className="d-flex align-items-center">
                                        <Button
                                            variant="outline-primary"
                                            onClick={() => loadHistory()}
                                            className="mr-2"
                                        >
                                            Atualizar
                                        </Button>
                                        <small className="text-muted">
                                            Última atualização {lastUpdatedDescription}
                                        </small>
                                    </div>
                                </Col>
                            </Row>

                            <Row className="my-3">
                                <Col xl={4} md={6} className="mb-3">
                                    <Card>
                                        <Card.Body>
                                            <h3 className="text-primary mb-1">{totalMessages}</h3>
                                            <span className="text-muted">Mensagens enviadas</span>
                                        </Card.Body>
                                    </Card>
                                </Col>
                                <Col xl={4} md={6} className="mb-3">
                                    <Card>
                                        <Card.Body>
                                            <h3 className="text-success mb-1">{autoMessages}</h3>
                                            <span className="text-muted">Automáticas</span>
                                        </Card.Body>
                                    </Card>
                                </Col>
                                <Col xl={4} md={6} className="mb-3">
                                    <Card>
                                        <Card.Body>
                                            <h3 className="text-info mb-1">{manualMessages}</h3>
                                            <span className="text-muted">Manuais</span>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            </Row>

                            <Tabs
                                activeKey={activeTab}
                                onSelect={(key) => setActiveTab(key || "table")}
                                className="mb-3"
                                mountOnEnter
                            >
                                <Tab eventKey="table" title="Tabela de mensagens">
                                    <Card className="mb-3">
                                        <Card.Body>
                                            <Row>
                                                <Col lg={4} className="mb-3">
                                                    <Form.Label>Buscar</Form.Label>
                                                    <Form.Control
                                                        placeholder="Lead, telefone ou conteúdo"
                                                        value={search}
                                                        onChange={(event) => setSearch(event.target.value)}
                                                    />
                                                </Col>
                                                <Col lg={4} md={6} className="mb-3">
                                                    <Form.Label>Tipo de envio</Form.Label>
                                                    <Form.Control
                                                        as="select"
                                                        value={typeFilter}
                                                        onChange={(event) => setTypeFilter(event.target.value)}
                                                    >
                                                        <option value="all">Todos</option>
                                                        <option value="automatic">Automáticos</option>
                                                        <option value="manual">Manuais</option>
                                                    </Form.Control>
                                                </Col>
                                                <Col lg={4} md={6} className="mb-3">
                                                    <Form.Label>Data</Form.Label>
                                                    <Form.Control
                                                        type="date"
                                                        value={dateFilter}
                                                        onChange={(event) => setDateFilter(event.target.value)}
                                                    />
                                                </Col>
                                            </Row>
                                        </Card.Body>
                                    </Card>

                                    <Card>
                                        <Card.Body className="table-responsive">
                                            <Table hover responsive>
                                                <thead>
                                                    <tr>
                                                        <th>Lead</th>
                                                        <th>Mensagem</th>
                                                        <th>Origem</th>
                                                        <th>Enviada em</th>
                                                        <th className="text-right">Ações</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {filteredHistory.length === 0 && (
                                                        <tr>
                                                            <td colSpan={5} className="text-center text-muted py-4">
                                                                Nenhuma mensagem encontrada.
                                                            </td>
                                                        </tr>
                                                    )}
                                                    {filteredHistory.map((historico) => {
                                                        const messageText = historico.mensagem || "";
                                                        const shortenedMessage =
                                                            messageText.length > 120
                                                                ? `${messageText.slice(0, 120)}...`
                                                                : messageText;

                                                        return (
                                                            <tr key={historico.id}>
                                                                <td className="align-middle" style={{ minWidth: 200 }}>
                                                                    <strong>{historico.lead?.name || "Lead removido"}</strong>
                                                                    <div className="text-muted">{historico.lead?.phone}</div>
                                                                </td>
                                                                <td
                                                                    className="align-middle"
                                                                    style={{ whiteSpace: "pre-wrap", maxWidth: 360 }}
                                                                >
                                                                    {shortenedMessage}
                                                                </td>
                                                                <td className="align-middle">
                                                                    <Badge variant={historico.foi_automatica ? "success" : "secondary"}>
                                                                        {historico.foi_automatica ? "Automática" : "Manual"}
                                                                    </Badge>
                                                                </td>
                                                                <td className="align-middle">
                                                                    {formatDateTime(historico.enviada_em)}
                                                                </td>
                                                                <td className="align-middle text-right">
                                                                    <Button
                                                                        variant="link"
                                                                        className="p-0"
                                                                        onClick={() => setSelectedMessage(historico)}
                                                                    >
                                                                        Ver detalhes
                                                                    </Button>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </Table>
                                        </Card.Body>
                                    </Card>
                                </Tab>

                                <Tab eventKey="conversations" title="Conversas por contato">
                                    <Row>
                                        <Col xl={4} className="mb-3">
                                            <Card className="h-100">
                                                <Card.Body className="d-flex flex-column">
                                                    <Form.Label>Buscar contato</Form.Label>
                                                    <Form.Control
                                                        placeholder="Nome ou telefone"
                                                        value={conversationSearch}
                                                        onChange={(event) => setConversationSearch(event.target.value)}
                                                        className="mb-3"
                                                    />

                                                    <div className="flex-grow-1 overflow-auto" style={{ maxHeight: 540 }}>
                                                        {filteredConversations.length === 0 ? (
                                                            <p className="text-muted mb-0">Nenhum contato encontrado.</p>
                                                        ) : (
                                                            <ListGroup>
                                                                {filteredConversations.map((conversation) => {
                                                                    const lastMessageFormatted = conversation.lastMessageDate
                                                                        ? formatDateTime(
                                                                            new Date(conversation.lastMessageDate).toISOString()
                                                                        )
                                                                        : "-";

                                                                    return (
                                                                        <ListGroup.Item
                                                                            key={conversation.leadId}
                                                                            action
                                                                            active={conversation.leadId === conversationLeadId}
                                                                            onClick={() => setConversationLeadId(conversation.leadId)}
                                                                            className="mb-2 rounded"
                                                                        >
                                                                            <div className="d-flex justify-content-between">
                                                                                <div>
                                                                                    <div className="font-weight-bold">
                                                                                        {conversation.leadName}
                                                                                    </div>
                                                                                    <div className="text-muted small">
                                                                                        {conversation.leadPhone}
                                                                                    </div>
                                                                                </div>
                                                                                <div className="text-right">
                                                                                    <Badge variant="primary" className="mb-1">
                                                                                        {conversation.messages.length} mensagens
                                                                                    </Badge>
                                                                                    <div className="text-muted small">{lastMessageFormatted}</div>
                                                                                </div>
                                                                            </div>
                                                                            <div className="text-muted small text-truncate">
                                                                                {conversation.lastMessageText || "(sem conteúdo)"}
                                                                            </div>
                                                                        </ListGroup.Item>
                                                                    );
                                                                })}
                                                            </ListGroup>
                                                        )}
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        </Col>

                                        <Col xl={8} className="mb-3">
                                            <Card className="h-100">
                                                <Card.Body className="d-flex flex-column">
                                                    {!selectedConversation ? (
                                                        <div className="text-center text-muted my-5">
                                                            Selecione um contato para visualizar todo o histórico.
                                                        </div>
                                                    ) : (
                                                        <>
                                                            <div className="d-flex justify-content-between align-items-start mb-3">
                                                                <div>
                                                                    <h5 className="mb-1">{selectedConversation.leadName}</h5>
                                                                    <div className="text-muted">{selectedConversation.leadPhone}</div>
                                                                </div>
                                                                <div className="text-right">
                                                                    <Badge variant="success" className="mr-1">
                                                                        {selectedConversation.automaticCount} automática(s)
                                                                    </Badge>
                                                                    <Badge variant="secondary">
                                                                        {selectedConversation.manualCount} manual(is)
                                                                    </Badge>
                                                                </div>
                                                            </div>

                                                            <div className="flex-grow-1 overflow-auto" style={{ maxHeight: 540 }}>
                                                                {selectedConversation.messages.map((message) => (
                                                                    <div key={message.id} className="mb-3 pb-3 border-bottom">
                                                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                                                            <Badge
                                                                                variant={message.foi_automatica ? "success" : "secondary"}
                                                                            >
                                                                                {message.foi_automatica ? "Automática" : "Manual"}
                                                                            </Badge>
                                                                            <small className="text-muted">
                                                                                {formatDateTime(message.enviada_em)}
                                                                            </small>
                                                                        </div>
                                                                        <div
                                                                            className="p-3 bg-light rounded"
                                                                            style={{ whiteSpace: "pre-wrap" }}
                                                                        >
                                                                            {message.mensagem || "(sem conteúdo)"}
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>

                                                            <Card className="mt-3">
                                                                <Card.Body>
                                                                    {!selectedLeadId ? (
                                                                        <p className="text-muted mb-0">
                                                                            Não é possível enviar novas mensagens para este contato.
                                                                        </p>
                                                                    ) : (
                                                                        <Form onSubmit={handleSendManualMessage}>
                                                                            <Form.Label>Enviar mensagem manual</Form.Label>
                                                                            <Form.Control
                                                                                as="textarea"
                                                                                rows={4}
                                                                                placeholder="Digite sua mensagem para este lead"
                                                                                value={manualMessage}
                                                                                onChange={(event) => setManualMessage(event.target.value)}
                                                                                disabled={sendingManual}
                                                                                className="mb-3"
                                                                            />
                                                                            <div className="d-flex justify-content-end">
                                                                                <Button
                                                                                    type="submit"
                                                                                    variant="primary"
                                                                                    disabled={sendingManual || !manualMessage.trim()}
                                                                                >
                                                                                    {sendingManual ? "Enviando..." : "Enviar mensagem"}
                                                                                </Button>
                                                                            </div>
                                                                        </Form>
                                                                    )}
                                                                </Card.Body>
                                                            </Card>
                                                        </>
                                                    )}
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    </Row>
                                </Tab>
                            </Tabs>

                            <MessagePreviewModal
                                show={Boolean(selectedMessage)}
                                onHide={() => setSelectedMessage(null)}
                                historico={selectedMessage}
                            />
                        </>
                    )}
                </div>
            </div>
        </Container>

    );
}